import { GFM, parser as baseParser } from "@lezer/markdown";
import { justOneSyntax } from "@justone/editor/syntax";

/**
 * 입력창의 멘션 태그: 고른 사람을 "@황승현 (kernel)"로 보여 주고, 보낼 때는 "@kernel"로 바꾼다.
 * 태그는 입력창 글 안의 자리(start~end)로 기억한다.
 */
export interface MentionTag {
  start: number;
  end: number;
  username: string;
  /** 백스페이스로 태그를 지우면 돌아갈 글 (고르기 전에 치던 "@황승") */
  query: string;
}

export interface TaggedText {
  text: string;
  tags: MentionTag[];
}

export const tagLabel = (u: { displayName: string; username: string }) => `@${u.displayName} (${u.username})`;

/**
 * 글이 바뀐 뒤 태그 자리를 옮긴다. 바뀐 곳에 걸친 태그는 버린다 (글자는 그대로 남고 멘션이 아니게 된다).
 * caret = 바뀐 뒤의 커서: 같은 글자가 이어질 때 어디가 바뀌었는지 가리는 데 쓴다.
 */
export function shiftTags(tags: MentionTag[], before: string, after: string, caret: number): MentionTag[] {
  if (before === after || tags.length === 0) return tags;
  const inserted = Math.max(0, after.length - before.length);
  const limit = Math.max(0, Math.min(before.length, after.length, caret - inserted));
  let p = 0;
  while (p < limit && before[p] === after[p]) p++;
  let s = 0;
  const maxS = Math.min(before.length, after.length) - p;
  while (s < maxS && before[before.length - 1 - s] === after[after.length - 1 - s]) s++;
  const oldEnd = before.length - s;
  const delta = after.length - before.length;
  return tags.flatMap((t) => {
    if (t.end <= p) return [t];
    if (t.start >= oldEnd) return [{ ...t, start: t.start + delta, end: t.end + delta }];
    return [];
  });
}

/** [from, to)를 insert로 바꾸고 태그를 옮긴다 (그 자리에 걸친 태그는 버린다) */
export function replaceRange(v: TaggedText, from: number, to: number, insert: string, keep?: MentionTag): TaggedText {
  const delta = insert.length - (to - from);
  const tags = v.tags.flatMap((t) => (t === keep ? [] : t.end <= from ? [t] : t.start >= to ? [{ ...t, start: t.start + delta, end: t.end + delta }] : []));
  return { text: v.text.slice(0, from) + insert + v.text.slice(to), tags };
}

/** 보낼 글: 태그를 "@아이디"로 */
export function toBody({ text, tags }: TaggedText): string {
  let out = text;
  for (const t of [...tags].sort((a, b) => b.start - a.start)) out = `${out.slice(0, t.start)}@${t.username}${out.slice(t.end)}`;
  return out;
}

const parser = baseParser.configure([GFM, justOneSyntax]);

/** 이미 쓴 글(고칠 때)의 "@아이디"를 태그로. 코드 안의 @는 그대로 */
export function fromBody(body: string, users: { username: string | null; displayName: string; active: boolean }[]): TaggedText {
  const found: { from: number; to: number; username: string; displayName: string }[] = [];
  parser.parse(body).iterate({
    enter(n) {
      if (n.name !== "Mention") return;
      const name = body.slice(n.from + 1, n.to).toLowerCase();
      const u = users.find((x) => x.username === name);
      if (u?.username) found.push({ from: n.from, to: n.to, username: u.username, displayName: u.displayName });
    },
  });
  let text = "";
  let pos = 0;
  const tags: MentionTag[] = [];
  for (const f of found) {
    text += body.slice(pos, f.from);
    const label = tagLabel(f);
    tags.push({ start: text.length, end: text.length + label.length, username: f.username, query: `@${f.displayName}` });
    text += label;
    pos = f.to;
  }
  return { text: text + body.slice(pos), tags };
}
